export type DeviceLocationStatus = boolean | null;

export const DEVICE_LOCATION_DISABLED_MESSAGE =
  "Turn on device location to continue this roleplay.";
export const DEVICE_LOCATION_UNKNOWN_MESSAGE =
  "Device location status could not be checked. Please retry.";

export function isNativeWebView() {
  return typeof window !== "undefined" && Boolean(window.ReactNativeWebView);
}

export function readDeviceLocationMessage(raw: unknown): {
  enabled: DeviceLocationStatus;
  requestId?: string;
} | null {
  if (typeof raw !== "string") return null;
  try {
    const message = JSON.parse(raw);
    if (message?.type !== "LOCATION_STATUS") return null;
    const payload = message.payload;
    if (
      !payload ||
      (typeof payload.enabled !== "boolean" && payload.enabled !== null)
    )
      return null;
    return {
      enabled: payload.enabled,
      requestId:
        typeof payload.requestId === "string" ? payload.requestId : undefined,
    };
  } catch {
    return null;
  }
}

let pendingCheck: Promise<DeviceLocationStatus> | undefined;
let requestSequence = 0;

/** Fresh native check; no cached result may authorize a location reading. */
export function checkDeviceLocation(): Promise<DeviceLocationStatus> {
  if (!isNativeWebView()) return Promise.resolve(null);
  if (pendingCheck) return pendingCheck;

  const requestId = `${Date.now()}-${++requestSequence}`;
  const check = new Promise<DeviceLocationStatus>((resolve) => {
    const finish = (enabled: DeviceLocationStatus) => {
      clearTimeout(timeout);
      window.removeEventListener("message", handleMessage);
      document.removeEventListener("message", handleMessage);
      resolve(enabled);
    };
    const handleMessage = (event: Event) => {
      const status = readDeviceLocationMessage((event as MessageEvent).data);
      if (status?.requestId === requestId) finish(status.enabled);
    };
    const timeout = setTimeout(() => finish(null), 5_000);
    window.addEventListener("message", handleMessage);
    document.addEventListener("message", handleMessage);
    try {
      window.ReactNativeWebView?.postMessage(
        JSON.stringify({
          type: "CHECK_LOCATION_STATUS",
          payload: { requestId },
        })
      );
    } catch {
      finish(null);
    }
  });
  pendingCheck = check;
  void check.finally(() => {
    if (pendingCheck === check) pendingCheck = undefined;
  });
  return check;
}
